import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageContainer from '~/shared/components/PageContainer';
import Typography from '~/shared/components/Typography';
import UtilizationChart from '~/shared/components/UtilizationCharts/UtilizationChart';
import UsageChart from '~/shared/components/UtilizationCharts/UsageChart';

import {
  apiFilterKey,
  reportsDateFilterKey,
  reportsFullGraphKey,
  reportsRefreshStateKey,
} from '../common/serachParamKeys';
import ReportsFilterSection from '../ReportsFilter/ReportsFilterSection';
import useUtilizationQueryParams from '../common/useUtilizationQueryParams';

import KPICards from './components/KPICards';
import OptimizationTable from './components/OptimizationTable';
import { buildSavingsQueryUrl } from './helpers';
import { useReportsSearchParamsHandlers } from './hooks/useReportsSearchParamsHandlers';
import { useOptimizationTableParams } from './hooks/useOptimizationTableParams';

const OPTIMIZATION_TABLE_DATA_LIMIT = 50;

export default function ReportsPage() {
  const [searchParams] = useSearchParams();
  const activeDateFilter = searchParams.get(reportsDateFilterKey);
  const activeRefreshState = searchParams.get(reportsRefreshStateKey);
  const apiFilters = searchParams.get(apiFilterKey);

  const [pageOffset, setPageOffset] = useState('0');

  const { utilizationQueryUrl, onRefetchUtilizationQuery } = useUtilizationQueryParams({
    utilizationDateFilterSearchParam: activeDateFilter,
    showFullGraphSearchParam: searchParams.get(reportsFullGraphKey),
    apiFiltersSearchParam: apiFilters,
    refreshStateSearchParams: activeRefreshState,
  });

  const {
    sortField,
    currentReportFilters,
    parsedFilters,
    optimizationTableQueryString,
    optimizationTableCountQueryString,
    filtersSignature,
    timeSignature,
  } = useOptimizationTableParams({
    activeDateFilter,
    apiFilters,
    dataLimit: OPTIMIZATION_TABLE_DATA_LIMIT,
    pageOffset,
  });

  const savingsQueryUrl = useMemo(() => buildSavingsQueryUrl(parsedFilters), [parsedFilters]);

  const {
    handleDateFilterChange,
    handleBaseFiltersChange,
    handleRefreshStateChange,
    handleChartDateFilterChange,
    handleOptimizationTableSortChange,
  } = useReportsSearchParamsHandlers({ setPageOffset });

  const handleOptimizationTablePageChange = (newPage: number) => {
    setPageOffset((newPage * OPTIMIZATION_TABLE_DATA_LIMIT).toString());
  };

  return (
    <PageContainer className="pb-0!">
      <KPICards queryUrl={savingsQueryUrl} utilizationQueryUrl={utilizationQueryUrl} />

      <div className="mt-3 border-b border-primary-70 sticky top-0 bg-white z-25">
        <ReportsFilterSection
          setPageOffset={setPageOffset}
          activeDateFilterTitle={activeDateFilter}
          currentBaseFilters={currentReportFilters}
          onDateFilterChange={handleDateFilterChange}
          onBaseFiltersChange={handleBaseFiltersChange}
        />
      </div>

      <div className="mt-[20px] px-[30px] py-[20px]">
        <div>
          <Typography fontWeight={500} variant="H5">
            Utilization over time
          </Typography>
          <div className="flex gap-[14px] mt-4">
            <UtilizationChart
              activeDateFilter={activeDateFilter || 'Last 3 hours'}
              activeRefreshState={activeRefreshState || ''}
              queryUrlString={utilizationQueryUrl}
              onClickRefresh={onRefetchUtilizationQuery}
              onSelectRefreshState={handleRefreshStateChange}
              onSelectDateFilter={handleChartDateFilterChange}
            />
            <UsageChart
              activeDateFilter={activeDateFilter || 'Last 3 hours'}
              activeRefreshState={activeRefreshState || ''}
              queryUrlString={utilizationQueryUrl}
              onClickRefresh={onRefetchUtilizationQuery}
              onSelectRefreshState={handleRefreshStateChange}
              onSelectDateFilter={handleChartDateFilterChange}
            />
          </div>
        </div>
      </div>
      <div className="px-[30px]">
        <OptimizationTable
          tableQueryUrl={`/api/v1/opt-log/actions?${optimizationTableQueryString}`}
          countQueryUrl={`/api/v1/opt-log/actions/count?${optimizationTableCountQueryString}`}
          onPageChange={handleOptimizationTablePageChange}
          onSortChange={handleOptimizationTableSortChange}
          dataLimit={OPTIMIZATION_TABLE_DATA_LIMIT}
          currentSortField={sortField}
          currentOffset={pageOffset}
          timeSignature={timeSignature}
          filtersSignature={filtersSignature}
        />
      </div>
    </PageContainer>
  );
}
